import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './UserCampaignHistoryPage.css';

const UserCampaignHistoryPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [activeCampaigns, setActiveCampaigns] = useState([]);
    const [campaignHistory, setCampaignHistory] = useState([]);
    const [selectedTab, setSelectedTab] = useState('overview');
    const [dateFilter, setDateFilter] = useState('all');

    useEffect(() => {
        if (user?.id) {
            fetchCampaignData();
        }
    }, [user?.id, dateFilter]);

    const fetchCampaignData = async () => {
        try {
            setLoading(true);
            
            // Sıralı olarak verileri getir (hata durumunda diğerleri çalışsın)
            
            // 1. İstatistikleri getir
            try {
                const statsResponse = await api.get(`/api/users/${user.id}/campaign-statistics`);
                if (statsResponse.data.success) {
                    setStatistics(statsResponse.data.data);
                } else {
                    console.warn('İstatistik verisi alınamadı:', statsResponse.data.error);
                    setStatistics({
                        total_campaigns: 0,
                        total_savings: 0,
                        active_campaigns: 0,
                        most_used_type: 'none'
                    });
                }
            } catch (error) {
                console.error('İstatistik hatası:', error);
                setStatistics({
                    total_campaigns: 0,
                    total_savings: 0,
                    active_campaigns: 0,
                    most_used_type: 'none'
                });
            }

            // 2. Aktif kampanyaları getir
            try {
                const activeResponse = await api.get(`/api/users/${user.id}/active-campaigns`);
                if (activeResponse.data.success) {
                    setActiveCampaigns(activeResponse.data.data || []);
                } else {
                    console.warn('Aktif kampanya verisi alınamadı:', activeResponse.data.error);
                    setActiveCampaigns([]);
                }
            } catch (error) {
                console.error('Aktif kampanya hatası:', error);
                setActiveCampaigns([]);
            }

            // 3. Kampanya geçmişini getir
            try {
                const historyResponse = await api.get(`/api/user-campaign-tracking/campaign-history`);
                if (historyResponse.data.success) {
                    setCampaignHistory(historyResponse.data.data.campaignUsage || []);
                } else {
                    console.warn('Kampanya geçmişi alınamadı:', historyResponse.data.error);
                    setCampaignHistory([]);
                }
            } catch (error) {
                console.error('Kampanya geçmişi hatası:', error);
                setCampaignHistory([]);
            }

        } catch (error) {
            console.error('Genel kampanya verileri hatası:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getCampaignTypeIcon = (type) => {
        const icons = {
            'cart': '🛒',
            'category': '📂',
            'coupon': '🎫',
            'flash_sale': '⚡',
            'package': '📦',
            'special_day': '🎉'
        };
        return icons[type] || '🎯';
    };

    const getCampaignTypeName = (type) => {
        const names = {
            'cart': 'Sepet Kampanyası',
            'category': 'Kategori Kampanyası',
            'coupon': 'Kupon Kampanyası',
            'flash_sale': 'Flash Sale',
            'package': 'Paket Kampanyası',
            'special_day': 'Özel Gün Kampanyası'
        };
        return names[type] || 'Kampanya';
    };

    const getStatusBadge = (status) => {
        const badges = {
            'active': { text: 'Aktif', class: 'status-active' },
            'refunded': { text: 'İade Edildi', class: 'status-refunded' },
            'cancelled': { text: 'İptal Edildi', class: 'status-cancelled' }
        };
        return badges[status] || { text: 'Bilinmiyor', class: 'status-unknown' };
    };

    if (loading) {
        return (
            <div className="campaign-history-loading">
                <div className="loading-spinner"></div>
                <p>Kampanya geçmişiniz yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className="campaign-history-page">
            <div className="campaign-history-header">
                <h1>💰 Kampanya Geçmişim</h1>
                <p>Tüm kampanya kullanımlarınızı ve tasarruflarınızı buradan takip edebilirsiniz.</p>
            </div>

            {/* İstatistik Kartları */}
            <div className="campaign-stats-grid">
                <div className="stat-card total-savings">
                    <div className="stat-icon">💰</div>
                    <div className="stat-content">
                        <h3>Toplam Tasarruf</h3>
                        <div className="stat-value">{formatCurrency(statistics?.total_savings || 0)}</div>
                        <div className="stat-subtitle">Tüm kampanyalardan</div>
                    </div>
                </div>

                <div className="stat-card active-campaigns">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-content">
                        <h3>Aktif Kampanyalar</h3>
                        <div className="stat-value">{statistics?.active_campaigns || 0}</div>
                        <div className="stat-subtitle">Devam eden kampanyalar</div>
                    </div>
                </div>

                <div className="stat-card total-campaigns">
                    <div className="stat-icon">📊</div>
                    <div className="stat-content">
                        <h3>Toplam Kampanya</h3>
                        <div className="stat-value">{statistics?.total_campaigns || 0}</div>
                        <div className="stat-subtitle">Kullanılan kampanya sayısı</div>
                    </div>
                </div>

                <div className="stat-card most-used">
                    <div className="stat-icon">{getCampaignTypeIcon(statistics?.most_used_type)}</div>
                    <div className="stat-content">
                        <h3>En Çok Kullanılan</h3>
                        <div className="stat-value-text">{getCampaignTypeName(statistics?.most_used_type)}</div>
                        <div className="stat-subtitle">Favori kampanya türünüz</div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="campaign-tabs">
                <button 
                    className={`tab-button ${selectedTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('overview')}
                >
                    📊 Genel Bakış
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'active' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('active')}
                >
                    🎯 Aktif Kampanyalar ({activeCampaigns.length})
                </button>
                <button 
                    className={`tab-button ${selectedTab === 'history' ? 'active' : ''}`}
                    onClick={() => setSelectedTab('history')}
                >
                    📜 Tüm Geçmiş ({campaignHistory.length})
                </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
                {selectedTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="overview-grid">
                            <div className="overview-card">
                                <h3>💸 Aylık Tasarruf</h3>
                                <div className="overview-value">{formatCurrency(statistics?.monthly_savings || 0)}</div>
                                <div className="overview-trend">Bu ay kazandığınız tutar</div>
                            </div>
                            
                            <div className="overview-card">
                                <h3>🔄 İade Edilen</h3>
                                <div className="overview-value">{statistics?.refunded_campaigns || 0}</div>
                                <div className="overview-trend">İade edilen kampanya sayısı</div>
                            </div>
                        </div>

                        {/* Son Aktiviteler */}
                        <div className="recent-activities">
                            <h3>🕐 Son Aktiviteler</h3>
                            <div className="activity-list">
                                {campaignHistory.slice(0, 5).map((campaign, index) => (
                                    <div key={index} className="activity-item">
                                        <div className="activity-icon">
                                            {getCampaignTypeIcon(campaign.campaign_type)}
                                        </div>
                                        <div className="activity-content">
                                            <div className="activity-title">
                                                {getCampaignTypeName(campaign.campaign_type)}
                                            </div>
                                            <div className="activity-subtitle">
                                                {formatCurrency(campaign.original_discount_amount)} tasarruf • {formatDate(campaign.created_at)}
                                            </div>
                                        </div>
                                        <div className={`activity-status ${getStatusBadge(campaign.status).class}`}>
                                            {getStatusBadge(campaign.status).text}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {selectedTab === 'active' && (
                    <div className="active-campaigns-tab">
                        {activeCampaigns.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">🎯</div>
                                <h3>Aktif kampanyanız bulunmuyor</h3>
                                <p>Yeni siparişlerinizde kampanyalardan yararlanmaya devam edin!</p>
                            </div>
                        ) : (
                            <div className="campaigns-grid">
                                {activeCampaigns.map((campaign, index) => (
                                    <div key={index} className="campaign-card active">
                                        <div className="campaign-header">
                                            <div className="campaign-icon">
                                                {getCampaignTypeIcon(campaign.campaign_type)}
                                            </div>
                                            <div className="campaign-info">
                                                <h4>{getCampaignTypeName(campaign.campaign_type)}</h4>
                                                <p className="campaign-name">{campaign.campaign_name}</p>
                                            </div>
                                            <div className="campaign-status active">Aktif</div>
                                        </div>
                                        <div className="campaign-details">
                                            <div className="detail-row">
                                                <span>Tasarruf:</span>
                                                <strong>{formatCurrency(campaign.original_discount_amount)}</strong>
                                            </div>
                                            <div className="detail-row">
                                                <span>Sipariş:</span>
                                                <span>#{campaign.order_number}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span>Tarih:</span>
                                                <span>{formatDate(campaign.usage_date)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {selectedTab === 'history' && (
                    <div className="history-tab">
                        <div className="history-filters">
                            <select 
                                value={dateFilter} 
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Tüm Zamanlar</option>
                                <option value="month">Bu Ay</option>
                                <option value="3months">Son 3 Ay</option>
                                <option value="year">Bu Yıl</option>
                            </select>
                        </div>

                        <div className="history-list">
                            {campaignHistory.map((campaign, index) => (
                                <div key={index} className="history-item">
                                    <div className="history-icon">
                                        {getCampaignTypeIcon(campaign.campaign_type)}
                                    </div>
                                    <div className="history-content">
                                        <div className="history-title">
                                            {getCampaignTypeName(campaign.campaign_type)}
                                        </div>
                                        <div className="history-details">
                                            <span>Tasarruf: {formatCurrency(campaign.original_discount_amount)}</span>
                                            <span>•</span>
                                            <span>{formatDate(campaign.created_at)}</span>
                                        </div>
                                    </div>
                                    <div className={`history-status ${getStatusBadge(campaign.status).class}`}>
                                        {getStatusBadge(campaign.status).text}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserCampaignHistoryPage;